import { useState } from 'react';
import { Popover } from '@mui/material';

import { ListFilterOption } from '@common/ui/ListFilters/common';
import ListFilterMultiSelect from '@common/ui/ListFilters/ListFilterMultiSelect';
import ListFilterSelectChipTrigger from '@common/ui/ListFilters/ListFilterSelectChipTrigger';

import { ASSESSMENT_REPORT_TIER_FILTER, ReportTierOptions, isReportTierOptions } from '@StandingAccess/types';
import { AssessmentTierWidgetIcon } from '@StandingAccess/components/AssessmentTierWidgetLabel';

interface AssessmentReportTierFilterProps {
  selected: string[];
  onSelect: (value: string[]) => void;
}

function useOptions({ selected }: Pick<AssessmentReportTierFilterProps, 'selected'>) {
  const validSelectedTiers = selected.filter((s): s is ReportTierOptions => isReportTierOptions(s));

  const options: ListFilterOption[] = Object.values(ReportTierOptions).map((tier) => ({
    value: tier,
    label: tier,
    icon: <AssessmentTierWidgetIcon type={tier} />,
  }));

  const selectedOptions = options.filter((o) => validSelectedTiers.includes(o.value as ReportTierOptions));

  return {
    options,
    selectedOptions,
  };
}

function useOpenClose() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>(undefined);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  return { opened: !!anchorEl, anchorEl, handleOpen, handleClose };
}

function useSelectHandler({ onSelect }: Pick<AssessmentReportTierFilterProps, 'onSelect'>) {
  const onSelectHandler = (value: ListFilterOption[]) => {
    const validTiers = value.map((v) => v.value).filter((v): v is ReportTierOptions => isReportTierOptions(v));
    onSelect(validTiers);
  };

  const onClear = () => {
    onSelect([]);
  };

  return { onSelectHandler, onClear };
}

export default function AssessmentReportTierFilter({ selected, onSelect }: AssessmentReportTierFilterProps) {
  const { onSelectHandler, onClear } = useSelectHandler({ onSelect });
  const { opened, anchorEl, handleOpen, handleClose } = useOpenClose();
  const { options, selectedOptions } = useOptions({ selected });

  return (
    <>
      <ListFilterSelectChipTrigger
        active={opened}
        label={ASSESSMENT_REPORT_TIER_FILTER.label}
        value={selectedOptions}
        onClear={onClear}
        onClick={handleOpen}
      />
      <Popover
        open={opened}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <ListFilterMultiSelect
          width={300}
          options={options}
          value={selectedOptions}
          onSelect={onSelectHandler}
          title={`Select ${ASSESSMENT_REPORT_TIER_FILTER.label} Filter`}
        />
      </Popover>
    </>
  );
}
